import React, { useEffect, useMemo, useState } from 'react';
import { userTabInitials } from '../utils/userInitials';
import { useToast } from '../context/ToastContext';

// Aligns with backend social design: mutual-accept friends; search is logged-in only
// and returns only id/username/initials; private = not searchable and no profile card for others.
const VISIBILITY_OPTIONS = [
  {
    value: 'public',
    label: 'Public',
    hint: 'Other signed-in users can find you in search and see your @username and avatar on your profile card.',
  },
  {
    value: 'friends_only',
    label: 'Friends only',
    hint: 'Only you (and later, accepted friends) can see your profile card. Watch history stays separate.',
  },
  {
    value: 'private',
    label: 'Private',
    hint: 'Others cannot load your profile by id, and your username will not appear in search. Only you see your full account details.',
  },
];

export default function SettingsPage({ onLogout, user, onUserUpdate }) {
  const { showToast } = useToast();
  const [username, setUsername] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [avatarInitials, setAvatarInitials] = useState('');
  const [profileVisibility, setProfileVisibility] = useState('public');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    setUsername((user?.username && String(user.username).trim()) || '');
    setFirst((user?.firstname && String(user.firstname).trim()) || '');
    setLast((user?.lastname && String(user.lastname).trim()) || '');
    setAvatarInitials(
      user?.avatar_initials != null ? String(user.avatar_initials).trim() : '',
    );
    const vis = user?.profile_visibility;
    setProfileVisibility(
      vis === 'friends_only' || vis === 'private' || vis === 'public' ? vis : 'public',
    );
    setSaveError(null);
  }, [
    user?.id,
    user?.username,
    user?.firstname,
    user?.lastname,
    user?.avatar_initials,
    user?.profile_visibility,
  ]);

  const savedUsername = (user?.username && String(user.username).trim()) || '';
  const savedFirst = (user?.firstname && String(user.firstname).trim()) || '';
  const savedLast = (user?.lastname && String(user.lastname).trim()) || '';
  const savedAvatar =
    user?.avatar_initials != null ? String(user.avatar_initials).trim() : '';
  const savedVisibility =
    user?.profile_visibility === 'friends_only' ||
    user?.profile_visibility === 'private' ||
    user?.profile_visibility === 'public'
      ? user.profile_visibility
      : 'public';

  const usernameDirty = username.trim() !== savedUsername;
  const firstDirty = first.trim() !== savedFirst;
  const lastDirty = last.trim() !== savedLast;
  const avatarDirty = avatarInitials.trim() !== savedAvatar;
  const visibilityDirty = profileVisibility !== savedVisibility;

  const hasChanges =
    usernameDirty || firstDirty || lastDirty || avatarDirty || visibilityDirty;

  const previewInitials = useMemo(
    () =>
      userTabInitials({
        ...user,
        avatar_initials: avatarInitials,
      }),
    [user, avatarInitials],
  );

  async function saveChanges() {
    if (!user?.id || !hasChanges || saving) return;
    const body = {};
    if (usernameDirty) body.username = username.trim();
    if (firstDirty) body.firstname = first.trim();
    if (lastDirty) body.lastname = last.trim();
    if (avatarDirty) body.avatar_initials = avatarInitials.trim();
    if (visibilityDirty) body.profile_visibility = profileVisibility;

    if (Object.keys(body).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch('/api/insertnewname', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      let data;
      try {
        data = await res.json();
      } catch (_parseErr) {
        throw new Error('Invalid response from server');
      }
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Failed to save changes');
      }
      await onUserUpdate?.();
      showToast('Changes saved');
    } catch (err) {
      console.error(err);
      setSaveError((err && err.message) || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="card flique-card settings-card">
      <div className="card-body">
        <h2 className="h4 mb-3">Settings</h2>

        <div className="mb-3">
          <label className="form-label small" htmlFor="settings-avatar-initials">
            Avatar initials
          </label>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span
              className="flique-tab-icon flique-tab-icon-avatar"
              aria-hidden="true"
            >
              {previewInitials}
            </span>
            <input
              id="settings-avatar-initials"
              type="text"
              className="form-control settings-avatar-input"
              maxLength={2}
              autoComplete="off"
              placeholder="Auto"
              value={avatarInitials}
              onChange={(e) => {
                setAvatarInitials(e.target.value.slice(0, 2));
                setSaveError(null);
              }}
            />
          </div>
          <p className="small text-muted mb-0 mt-1">
            Up to two characters for the tab icon. Leave empty to use your name or username.
          </p>
        </div>

        <div className="mb-3">
          <label className="form-label small" htmlFor="settings-username">
            Username (@handle)
          </label>
          <input
            id="settings-username"
            type="text"
            className="form-control"
            placeholder="your_handle"
            autoComplete="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setSaveError(null);
            }}
          />
          <p className="small text-muted mb-0 mt-1">
          Friends can search your @handle to find your profile.
          </p>
        </div>
        <div className="mb-3">
          <label className="form-label small" htmlFor="settings-firstname">
            First name
          </label>
          <input
            id="settings-firstname"
            type="text"
            className="form-control"
            placeholder="First name"
            value={first}
            onChange={(e) => {
              setFirst(e.target.value);
              setSaveError(null);
            }}
          />
        </div>
        <div className="mb-3">
          <label className="form-label small" htmlFor="settings-lastname">
            Last name
          </label>
          <input
            id="settings-lastname"
            type="text"
            className="form-control"
            placeholder="Last name"
            value={last}
            onChange={(e) => {
              setLast(e.target.value);
              setSaveError(null);
            }}
          />
        </div>
        <div className="mb-3">
          <label className="form-label small" htmlFor="settings-profile-visibility">
            Profile visibility
          </label>
          <select
            id="settings-profile-visibility"
            className="form-select"
            value={profileVisibility}
            onChange={(e) => {
              setProfileVisibility(e.target.value);
              setSaveError(null);
            }}
          >
            {VISIBILITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="small text-muted mb-0 mt-1">
            {VISIBILITY_OPTIONS.find((o) => o.value === profileVisibility)?.hint}
          </p>
        </div>
        {saveError && (
          <p className="small text-danger mb-3">{saveError}</p>
        )}
        <div className="mb-4 pt-2">
          <button
            type="button"
            className="btn btn-flique-secondary w-100"
            disabled={!hasChanges || saving || !user?.id}
            onClick={saveChanges}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
        <div className="mb-0">
          <button
            type="button"
            className="btn btn-flique w-100"
            onClick={onLogout}
          >
            Log out
          </button>
        </div>
      </div>
    </section>
  );
}
